import { bindBoundaryAdviceFetch } from '../gf/bind-boundary-advice-fetch.js';
import { bindBoundaryRoiFetch } from '../gf/bind-boundary-roi-fetch.js';
import { bindHelperSideMenu } from '../gf/bind-helper-side-menu.js';
import {
	bindSidebarStepsNav,
	destroySidebarStepsNav,
} from '../gf/bind-sidebar-steps-nav.js';

let activeApp = null;
let lifecycleListenersRegistered = false;

/**
 * Responsibility:
 * - Normalize Gravity Forms `gform/post_render` event payload for this configurator.
 *
 * Touches:
 * - Reads `event.detail.formId` and `event.detail.currentPage`.
 * - Returns numeric values consumed by event rebind handling.
 *
 * Guarantees:
 * - Always returns an object with numeric `formId` and `currentPage` keys.
 */
function parsePostRenderEvent(event) {
	const detail = event && typeof event === 'object' && 'detail' in event ? event.detail : null;
	const detailObject = detail && typeof detail === 'object' ? detail : {};
	const formId = Number(detailObject.formId ?? 0);
	const currentPage = Number(detailObject.currentPage ?? 0);

	return {
		formId,
		currentPage,
	};
}

/**
 * Responsibility:
 * - Normalize Gravity Forms `gform/ajax/post_page_change` event payload.
 *
 * Touches:
 * - Reads `event.detail.formId` and `event.detail.pageNumber`.
 *
 * Guarantees:
 * - Always returns numeric `formId` and `currentPage`.
 */
function parseAjaxPostPageChangeEvent(event) {
	const detail = event && typeof event === 'object' && 'detail' in event ? event.detail : null;
	const detailObject = detail && typeof detail === 'object' ? detail : {};
	const formId = Number(detailObject.formId ?? 0);
	const currentPage = Number(detailObject.pageNumber ?? 0);

	return {
		formId,
		currentPage,
	};
}

/**
 * Responsibility:
 * - Persist currently rendered GF page number into shared app state.
 *
 * Touches:
 * - Writes `app.currentPage`.
 *
 * Guarantees:
 * - Stores only positive finite page numbers.
 */
function writeCurrentPage(app, currentPage) {
	const pageNumber = Number(currentPage || 0);
	if (!Number.isFinite(pageNumber) || pageNumber <= 0) {
		return;
	}

	app.currentPage = Math.trunc(pageNumber);
}

/**
 * Responsibility:
 * - Bind the active configurator app to the current page DOM.
 *
 * Touches:
 * - Runs all page-scoped configurator binders against the latest `activeApp`.
 *
 * Guarantees:
 * - Rebinds sidebar, helper, and result handlers for each freshly rendered configurator page.
 */
function bindActiveApp() {
	if (!activeApp) {
		return;
	}

	bindBoundaryAdviceFetch(activeApp);
	bindBoundaryRoiFetch(activeApp);
	bindHelperSideMenu(activeApp);
	bindSidebarStepsNav(activeApp);
}

/**
 * Responsibility:
 * - Attach shared Gravity Forms lifecycle listeners once for the configurator runtime.
 *
 * Touches:
 * - Registers document listeners for GF render/change hooks.
 * - Reuses the module-level `activeApp` reference on every callback.
 *
 * Guarantees:
 * - Document listeners are attached at most once per browser session.
 * - Later swup page views reuse the same listeners without duplicating them.
 */
function ensureLifecycleListeners() {
	if (lifecycleListenersRegistered) {
		return;
	}

	document.addEventListener('gform/post_render', (event) => {
		if (!activeApp) {
			return;
		}

		const payload = parsePostRenderEvent(event);
		if (Number(payload.formId) !== Number(activeApp.config.formId)) {
			return;
		}
		writeCurrentPage(activeApp, payload.currentPage);

		activeApp.logger.debug('GF post render; rebinding boundary handlers.', {
			formId: activeApp.config.formId,
			currentPage: Number(payload.currentPage || 0),
		});
		bindActiveApp();
	});

	document.addEventListener('gform/ajax/post_page_change', (event) => {
		if (!activeApp) {
			return;
		}

		const payload = parseAjaxPostPageChangeEvent(event);
		if (Number(payload.formId) !== Number(activeApp.config.formId)) {
			return;
		}
		writeCurrentPage(activeApp, payload.currentPage);

		activeApp.logger.debug('GF ajax post page change; rebinding boundary handlers.', {
			formId: activeApp.config.formId,
			currentPage: Number(payload.currentPage || 0),
		});
		bindActiveApp();
	});

	document.addEventListener('gform/theme/scripts_loaded', () => {
		if (!activeApp) {
			return;
		}

		activeApp.logger.debug('GF scripts loaded; rebinding boundary handlers.', {
			formId: activeApp.config.formId,
		});
		bindActiveApp();
	});

	lifecycleListenersRegistered = true;
}

/**
 * Responsibility:
 * - Initialize configurator bindings for the current page lifecycle entry.
 *
 * Touches:
 * - Stores the latest app instance in module state.
 * - Ensures shared GF document listeners exist and rebinds current page DOM handlers.
 *
 * Guarantees:
 * - Swup page visits reuse one set of global listeners while still binding the fresh page markup.
 */
export function initConfiguratorApp(app) {
	activeApp = app;

	app.logger.debug('Registering configurator events.', {
		formId: app.config.formId,
	});

	ensureLifecycleListeners();
	bindActiveApp();
}

/**
 * Responsibility:
 * - Clear the active configurator app when the current page is being removed.
 *
 * Touches:
 * - Resets the module-level `activeApp` reference used by shared GF lifecycle listeners.
 *
 * Guarantees:
 * - Shared listeners become inert until a later page init provides a fresh app instance.
 */
export function destroyConfiguratorApp() {
	if (activeApp) {
		destroySidebarStepsNav(activeApp);
	}

	activeApp = null;
}
